import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useScheduledPayments } from "@/hooks/usePaymentTracking";
import { Calendar, DollarSign } from "lucide-react";
import { format, addDays, startOfWeek } from "date-fns";

export function UpcomingPaymentsCalendar({ workspaceId }: { workspaceId: string }) {
  const { data: scheduledPayments } = useScheduledPayments(workspaceId);

  // Group payments by week
  const upcomingWeeks = Array.from({ length: 4 }, (_, i) => {
    const weekStart = addDays(startOfWeek(new Date()), i * 7);
    return {
      start: weekStart,
      label: format(weekStart, "MMM d"),
      payments: scheduledPayments?.filter((p) => {
        const paymentDate = new Date(p.scheduled_date);
        return (
          paymentDate >= weekStart &&
          paymentDate < addDays(weekStart, 7)
        );
      }) || [],
    };
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          Upcoming Payments Calendar
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {upcomingWeeks.map((week, idx) => (
            <div key={idx} className="space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Week of {week.label}</h4>
                <Badge variant="outline">{week.payments.length} payments</Badge>
              </div>
              
              {week.payments.length > 0 ? (
                <div className="space-y-2">
                  {week.payments.map((payment) => (
                    <div
                      key={payment.id}
                      className="flex items-center justify-between p-3 rounded-lg border bg-muted/20"
                    >
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium">
                            {format(new Date(payment.scheduled_date), "EEE, MMM d")}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Bill ID: {payment.bill_id}
                          </p>
                        </div>
                      </div>
                      <Badge>Scheduled</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-2">
                  No payments scheduled
                </p>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
